import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { AppBar, Tabs, Tab, Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import FileDownloadIcon from '@mui/icons-material/GetApp';
import { useDispatch, useSelector } from 'react-redux';
import TabPanel from 'src/components/TabPanel/TabPanel';
import {
  loadFiscalCapitalBalance,
  loadFiscalInterestBalance,
  downloadFiscalInterestBalanceAction,
  downloadFiscalCapitalBalanceAction
} from 'src/store/actions/contractActions';
import {
  selectFiscalInterestBalance,
  selectFiscalCapitalBalance
} from 'src/store/selectors/contractSelectors';
import {
  ContractDetailTabsIndex,
  FiscalBalanceSubTabsIndex
} from '../../../../utils/constants/contracts';
import useStyles from '../styles';
import FiscalCapitalBalanceSchedule from './FiscalCapitalBalanceSchedule';
import FiscalInterestBalanceSchedule from './FiscalInterestBalanceSchedule';

const FiscalBalanceTabs = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { id, '*': splat } = useParams();
  const [tab, subtab] = (splat || '').split('/');
  const [value, setValue] = useState(false);
  const interestBalanceSchedule = useSelector(selectFiscalInterestBalance);
  const fiscalCapitalBalanceSchedule = useSelector(selectFiscalCapitalBalance);

  const handleChange = useCallback(
    (_, newValue) => {
      setValue(newValue);
      navigate(`/contracts/${id}/${ContractDetailTabsIndex.FISCAL_BALANCE}/${newValue}`);
    },
    [navigate, id]
  );

  const handleExcelExport = useCallback(() => {
    if (value === FiscalBalanceSubTabsIndex.CAPITAL) {
      dispatch(downloadFiscalCapitalBalanceAction(id));
    } else if (value === FiscalBalanceSubTabsIndex.INTEREST) {
      dispatch(downloadFiscalInterestBalanceAction(id));
    }
  }, [dispatch, id, value]);

  useEffect(() => {
    if (tab === ContractDetailTabsIndex.FISCAL_BALANCE && !subtab) {
      setValue(FiscalBalanceSubTabsIndex.CAPITAL);
      navigate(
        `/contracts/${id}/${ContractDetailTabsIndex.FISCAL_BALANCE}/${FiscalBalanceSubTabsIndex.CAPITAL}`
      );
    }
    if (subtab) {
      if (
        Object.values(FiscalBalanceSubTabsIndex).includes(subtab)
      ) {
        setValue(subtab);
      } else {
        navigate(
          `/contracts/${id}/${ContractDetailTabsIndex.FISCAL_BALANCE}/${FiscalBalanceSubTabsIndex.CAPITAL}`
        );
      }
    }
  }, [tab, subtab, id, navigate]);

  useEffect(() => {
    if (value === FiscalBalanceSubTabsIndex.CAPITAL) {
      dispatch(loadFiscalCapitalBalance({ id }));
    } else if (value === FiscalBalanceSubTabsIndex.INTEREST) {
      dispatch(loadFiscalInterestBalance({ id }));
    }
  }, [value, id, dispatch]);

  return (
    <Box sx={{ flexGrow: 1, mt: 3 }}>
      <AppBar
        variant="outlined"
        position="static"
        sx={{
          border: 'none',
          borderBottom: '1px solid rgba(43,45,66,0.1)',
          background: 'transparent',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Tabs
          indicatorColor="primary"
          textColor="primary"
          value={value}
          onChange={handleChange}
        >
          <Tab
            label={t('COMMON.CAPITAL')}
            value={FiscalBalanceSubTabsIndex.CAPITAL}
          />
          <Tab
            label={t('COMMON.INTEREST')}
            value={FiscalBalanceSubTabsIndex.INTEREST}
          />
        </Tabs>
        <Box sx={{ pr: 2 }}>
          <Button
            className={clsx(classes.buttons, classes.outlined)}
            onClick={handleExcelExport}
            color="primary"
            title={t('COMMON.DOWNLOAD_EXCEL')}
          >
            <FileDownloadIcon className={classes.icon} />
          </Button>
        </Box>
      </AppBar>
      <TabPanel value={value} index={FiscalBalanceSubTabsIndex.CAPITAL}>
        <FiscalCapitalBalanceSchedule
          fiscalCapitalBalanceSchedule={fiscalCapitalBalanceSchedule}
        />
      </TabPanel>
      <TabPanel value={value} index={FiscalBalanceSubTabsIndex.INTEREST}>
        <FiscalInterestBalanceSchedule
          interestBalanceSchedule={interestBalanceSchedule}
        />
      </TabPanel>
    </Box>
  );
};

export default FiscalBalanceTabs;

